import React, { useState, useEffect } from 'react';
import { CenteredLoader, CenteredModal, GenericButton } from '@app/core';
import { createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';
import { ErrorMsg } from '@app/form';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    width: '100%',
    height: theme.maxContentWidth.largeReduced,
    position: 'relative'
  },
  errorContainer: {
    padding: 20,
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%'
  },
  iframe: {
    width: '100%',
    height: '100%',
    border: '0',
    borderRadius: '8px',
    display: 'block'
  },
  displayNone: {
    display: 'none'
  }
}));

interface ModalProps {
  calendarUrl: string;
  isOpen: boolean;
  onRequestClose: () => any;
}

export const GoogleAppointmentModal = ({
   calendarUrl,
   isOpen,
   onRequestClose,
 }: ModalProps) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsLoading(true);
      setHasError(false);
    }
  }, [isOpen]);

  const handleIframeLoad = () => {
    setIsLoading(false);
    setHasError(false);
  };

  const handleIframeError = () => {
    setIsLoading(false);
    setHasError(true);
  };

  const handleFallbackClick = () => {
    window.open(calendarUrl, '_blank');
    onRequestClose();
  };

  return (
    <CenteredModal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      xxlarge
    >
      <div className={classes.container}>
        {isLoading && (
          <CenteredLoader />
        )}

        {hasError ? (
          <div className={classes.errorContainer}>
            <ErrorMsg error={t('calendar.sync.connectionFailure')} />
            <GenericButton onClick={handleFallbackClick}>
              {t('studySetup.helpBlock.button')}
            </GenericButton>
          </div>
        ) : (
            <iframe
              title='calendar'
              key={isOpen ? 'open' : 'closed'}
              src={calendarUrl}
              className={classnames(classes.iframe, {
                [classes.displayNone]: isLoading
              })}
              onLoad={handleIframeLoad}
              onError={handleIframeError}
              allow="camera; microphone; geolocation; autoplay; encrypted-media; fullscreen"
              sandbox="allow-scripts allow-same-origin allow-popups allow-forms allow-popups-to-escape-sandbox"
              referrerPolicy="strict-origin-when-cross-origin"
            />
        )}
      </div>
    </CenteredModal>
  );
};
